// src/components/AddMaterial.js
import React, { useState, useContext, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { useMediaQuery } from "react-responsive";
import { baseURL } from "../../utils/api";

const AddMaterial = ({ onAddSuccess }) => {
  const toast = useRef(null);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { user, token } = useContext(AuthContext);
  const [nazwa, setNazwa] = useState("");
  // Lista materiałów – rekordy będą zawierały dodatkowo pole "dostepny"
  const [materialy, setMaterialy] = useState([]);
  // Tryb edycji – gdy rekord jest edytowany, umożliwiamy zmianę statusu dostępności
  const [editingMaterial, setEditingMaterial] = useState(null);
  // Kontrola wyświetlania – domyślnie pokazujemy tylko dostępne materiały
  const [showAll, setShowAll] = useState(false);

  // Dodawanie nowego materiału – nowe rekordy domyślnie z dostepny: true
  const handleSubmit = () => {
    const newMaterial = { nazwa, dostepny: true };
    axios
      .post(`${baseURL}api/materialyAdd`, newMaterial, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log("Dodano materiał:", response.data);
        setNazwa("");
        setMaterialy([...materialy, response.data]);
        onAddSuccess();
      })
      .catch((error) => {
        console.error("Błąd dodawania materiału:", error);
      });
  };

  useEffect(() => {
    axios
      .get(`${baseURL}api/materialy`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setMaterialy(response.data))
      .catch((error) => console.error("Błąd pobierania materiałów:", error));
  }, [token]);

  // Zamiast usuwania, zmieniamy status dostępności
  // Zmiana statusu dostępności będzie możliwa tylko w trybie edycji
  const handleToggleAvailability = (material) => {
    if (editingMaterial && editingMaterial.id === material.id) {
      setEditingMaterial({
        ...editingMaterial,
        dostepny: !editingMaterial.dostepny,
      });
    }
  };

  // Rozpoczęcie edycji rekordu materiału
  const handleEditClick = (material) => {
    setEditingMaterial({ ...material });
  };

  // Zapis zmian (w tym zmiana statusu dostępności)
  const handleSaveClick = () => {
    axios
      .put(`${baseURL}api/materialy/${editingMaterial.id}`, editingMaterial, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        console.log("Zaktualizowano materiał:", response.data);
        setMaterialy(
          materialy.map((m) =>
            m.id === editingMaterial.id ? response.data : m
          )
        );
        setEditingMaterial(null);
        toast.current.show({
          severity: "success",
          summary: "Zapisano zmiany",
          life: 3000,
        });
      })
      .catch((error) => {
        console.error("Błąd aktualizacji materiału:", error);
      });
  };

  const handleCancelClick = () => {
    setEditingMaterial(null);
    toast.current.show({
      severity: "info",
      summary: "Anulowano edycję",
      life: 3000,
    });
  };

  // Renderowanie edytowalnych komórek lub zwykłych
  const textBodyTemplate = (rowData, field) => {
    if (editingMaterial && editingMaterial.id === rowData.id) {
      return (
        <InputText
          value={editingMaterial[field] || ""}
          onChange={(e) =>
            setEditingMaterial({ ...editingMaterial, [field]: e.target.value })
          }
        />
      );
    }
    return rowData[field];
  };

  // Kolumna "Dostępny" – checkbox edytowalny tylko w trybie edycji, poza edycją wyświetlamy jako disabled
  const availabilityBodyTemplate = (rowData) => {
    if (editingMaterial && editingMaterial.id === rowData.id) {
      return (
        <Checkbox
          checked={editingMaterial.dostepny}
          onChange={() => handleToggleAvailability(rowData)}
        />
      );
    } else {
      return <Checkbox checked={rowData.dostepny} disabled />;
    }
  };

  // Kolumna z przyciskami edycji – podczas edycji przyciski zapisu/ anulowania
  const actionBodyTemplate = (rowData) => {
    if (editingMaterial && editingMaterial.id === rowData.id) {
      return (
        <React.Fragment>
          <Button
            icon="pi pi-save"
            className="p-button-success p-mr-2"
            onClick={handleSaveClick}
          />
          <Button
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={handleCancelClick}
          />
        </React.Fragment>
      );
    } else {
      return (
        <Button
          // label="Edytuj"      
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => handleEditClick(rowData)}
        // className="p-button-warning"
        />
      );
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="add-form">
        <h3>Dodaj nowy materiał</h3>
        <div className="add-input">
          <label>Nazwa:</label>
          <InputText value={nazwa} onChange={(e) => setNazwa(e.target.value)} />
        </div>
        <Button
          label="Dodaj materiał"
          onClick={handleSubmit}
          disabled={!nazwa}
        />
      </div>
      <Button
        label={showAll ? "Pokaż tylko dostępne" : "Pokaż wszystkie"}
        onClick={() => setShowAll(!showAll)}
        style={{ margin: "1rem 0" }}
      />
      {isMobile && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <i>
            W celu uzyskania lepszej widoczności danych, skorzystaj z orientacji
            poziomej
          </i>
        </div>
      )}
      <DataTable
        value={showAll ? materialy : materialy.filter((m) => m.dostepny)}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 15]}
        selectionMode="single"
        style={{ marginTop: "30px" }}
      >
        <Column
          field="nazwa"
          header="Nazwa"
          sortable
          body={(rowData) => textBodyTemplate(rowData, "nazwa")}
        />
        {/* Możesz dodać inne kolumny, jeśli masz je w danych (np. kod) */}
        <Column
          header="Dostępny"
          field="dostepny"
          sortable
          body={availabilityBodyTemplate}
          headerStyle={{ textAlign: "center", width: "7rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
        {user?.role === "Administrator" && (
          <Column header="Akcje" body={actionBodyTemplate} />
        )}
      </DataTable>
    </div>
  );
};

export default AddMaterial;
